import { useMemo, useState } from 'react';

import {
  CheckboxGroup,
  ProfileFormAutoComplete,
  ProfileFormSelect,
  ProfileFormTextField,
} from '@backrs_ui/common-ui';

import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  ProfileValueOptionResponse,
  ProfileFields,
  useFetchUserProfileInfoQuery,
  useFetchProfileValueOptionsQuery,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { USER_PROFILE_FIELDS } from '@backrs_ui/grow-ui/src/redux/profile/profile.constants';

import { Box, Typography, SxProps, Theme } from '@mui/material';
import { Controller, useForm, FormProvider } from 'react-hook-form';

import { DynamicInput } from './atoms/DynamicInput';
import { ProfileImageField } from './atoms/ProfileImageField';
import { ProfileToggleField } from './atoms/ProfileToggleField';
import { ProfileDocumentView } from './molecules/ProfileDocumentView';
import { ProfileVideoView } from './molecules/ProfileVideoView';
import { ProfileApiAutoCompleteField } from './ProfileApiAutoCompleteField';
import { ProfileFieldConfig, AcademicMetricValue } from './types/profile.types';
import {
  getDefaultValue,
  validateValue,
  formatUrl,
  determineIfInputDisabled,
  handleApTestChange,
  handleEntranceExamChange,
  handleAcademicMajorSelection,
} from './utils/fields.helpers';

type ProfileEditSectionItemProps = Readonly<{
  field: ProfileFieldConfig;
  options: ProfileValueOptionResponse[ProfileFields];
  error?: string;
}>;

export const ProfileEditSectionItem = ({ field, options, error }: ProfileEditSectionItemProps) => {
  const form = useForm();
  const { control, getValues, setValue } = form;

  const { data: profileValueOptions } = useFetchProfileValueOptionsQuery(undefined);

  const [optimisticCheckboxValues, setOptimisticCheckboxValues] = useState<string[]>(
    field.defaultValue
      ? Array.isArray(field.defaultValue)
        ? field.defaultValue
            .map((opt) => (typeof opt === 'string' ? opt : (opt?.id?.toString() ?? '')))
            .filter(Boolean)
        : [
            typeof field.defaultValue === 'string'
              ? field.defaultValue
              : (field.defaultValue?.id?.toString() ?? ''),
          ].filter(Boolean)
      : [],
  );

  const { user } = useAppSelector((state) => state.user);

  const { data: userProfile } = useFetchUserProfileInfoQuery(USER_PROFILE_FIELDS);

  // Format regular options
  const formattedOptions = useMemo(() => {
    if (field.customOptions) {
      return field
        .customOptions({ options: profileValueOptions, profile: userProfile })
        .map((opt) => ({
          label: opt.name,
          value: opt.value ?? opt.id.toString(),
        }));
    }

    if (!options) return [];

    if (Array.isArray(options)) {
      return options.map((opt) => {
        if (!opt) return { label: '', value: '' };
        if ('club' in opt) return { label: opt.club, value: opt.id.toString() };
        if ('language' in opt) return { label: opt.language, value: opt.id.toString() };
        if ('school' in opt) return { label: opt.school, value: opt.id.toString() };

        if ('media_url' in opt) {
          return { label: opt.title, value: opt.id.toString(), opt };
        }

        return { label: opt.name, value: opt.id.toString() };
      });
    }

    if (typeof options === 'object' && field.key in options) {
      const value = options[field.key as keyof typeof options];

      if (!value) return [];

      const isNameAndIdPresent =
        typeof value === 'object' && value !== null && ('name' in value || 'id' in value);

      if (isNameAndIdPresent) {
        return [
          {
            label: (value as { name?: string }).name || '',
            value: (value as { id?: number }).id?.toString() || '',
          },
        ];
      }

      return [];
    }

    return [];
  }, [options, field, profileValueOptions, userProfile]);

  switch (field.type.variant) {
    case 'TOGGLE': {
      return (
        <ProfileToggleField
          field={{ ...field, defaultValue: field.defaultValue }}
          control={control}
          setValue={setValue}
        />
      );
    }

    case 'API_AUTOCOMPLETE': {
      return (
        <FormProvider {...form}>
          <Box sx={itemContainerSx}>
            <ProfileApiAutoCompleteField
              field={field}
              control={control}
              disabled={determineIfInputDisabled(field, userProfile, profileValueOptions)}
              error={error}
            />
          </Box>
        </FormProvider>
      );
    }
    case 'AUTOCOMPLETE': {
      const defaultValue = getDefaultValue(field.defaultValue);

      return (
        <Box sx={itemContainerSx}>
          {!field.hideInput && (
            <Controller
              name={field.key}
              control={control}
              defaultValue={defaultValue}
              render={({ field: controllerField, fieldState: { error } }) => (
                <ProfileFormAutoComplete
                  key={field.key}
                  disabled={determineIfInputDisabled(field, userProfile, profileValueOptions)}
                  isMultiple={field.maxSelectedOptions ? field.maxSelectedOptions > 1 : true}
                  autoCompleteOptions={formattedOptions}
                  options={formattedOptions}
                  chipType={field.chipType}
                  showSearch={field.showSearch ?? false}
                  field={{
                    name: field.key,
                    autoCompleteEndpoint: field.key,
                    manualFilterOptions:
                      field.type.variant === 'AUTOCOMPLETE'
                        ? field.type.manualFilterOptions
                        : false,
                    placeholder: field.placeholder,
                    selectOptions: {
                      type: 'MULTIPLE',
                      options: formattedOptions,
                    },
                  }}
                  inlineChip={field.inlineChip}
                  controllerField={{
                    ...controllerField,
                    value: defaultValue || controllerField.value, // use the redux value so we listen for changes in dynamic fields
                    onChange: (e) => {
                      controllerField.onChange(e);
                      const values = Array.isArray(e) ? e : [e];

                      if (field.key === 'academicApTests') {
                        handleApTestChange({
                          field,
                          values,
                          getValues,
                          setRHFValue: setValue,
                          clearZeroScores: false,
                        });

                        return;
                      }

                      if (field.key === 'academicMajors') {
                        handleAcademicMajorSelection(values, field, getValues);

                        return;
                      }

                      if (field.key === 'academicEntranceExams') {
                        handleEntranceExamChange(field, values, getValues, profileValueOptions);

                        return;
                      }

                      field.onChange(values.map((v) => (typeof v === 'string' ? Number(v) : v)));
                    },
                  }}
                  groupedFields={{ [field.key]: [field as { name?: string }] }}
                  index={0}
                  error={error}
                  groupKey={field.key}
                />
              )}
            />
          )}
          {field.createDynamicInput && Array.isArray(field.defaultValue) && (
            <Box sx={dynamicInputsContainerSx}>
              {field.defaultValue.map(
                (option) =>
                  typeof option !== 'string' && (
                    <DynamicInput
                      key={`${field.key}_${option.id}`}
                      selectedOption={option}
                      field={field}
                      control={control}
                      validateValue={validateValue}
                      getValues={getValues}
                      setRHFValue={setValue}
                      disabled={determineIfInputDisabled(field, userProfile, profileValueOptions)}
                    />
                  ),
              )}
            </Box>
          )}
        </Box>
      );
    }
    case 'DOCUMENT': {
      if (!field.type.fileUploadConfig || !field.storyDocuments) {
        return null;
      }

      return (
        <ProfileDocumentView
          fileUploadConfig={field.type.fileUploadConfig}
          storyDocuments={field.storyDocuments}
        />
      );
    }
    case 'STORY_VIDEO': {
      if (!field.type.fileUploadConfig || !field.storyDocuments) {
        return null;
      }

      return (
        <ProfileVideoView
          fileUploadConfig={field.type.fileUploadConfig}
          storyDocuments={field.storyDocuments}
          user={user}
        />
      );
    }
    case 'MULTIPLE_SELECT':
    case 'SINGLE_SELECT': {
      const currentValue = getDefaultValue(field.defaultValue);

      return (
        <Box sx={itemContainerSx}>
          <Controller
            key={`${field.key}-${JSON.stringify(currentValue)}`}
            name={field.key}
            control={control}
            render={({ field: controllerField, fieldState: { error } }) => (
              <ProfileFormSelect
                key={field.key}
                disabled={determineIfInputDisabled(field, userProfile, profileValueOptions)}
                isMultiple={field.type.variant === 'MULTIPLE_SELECT'}
                options={formattedOptions}
                chipType={field.chipType}
                textLeadingAdornment={field.textLeadingAdornment}
                showSearch={field.showSearch}
                showHoverDelete={!(field.type.variant === 'SINGLE_SELECT' && field.type.hideClear)}
                field={{
                  name: field.key,
                  placeholder: field.placeholder ?? '',
                  selectOptions: {
                    type: field.type.variant === 'MULTIPLE_SELECT' ? 'MULTIPLE' : 'SINGLE',
                    options: formattedOptions,
                  },
                }}
                controllerField={{
                  ...controllerField,
                  // Use the current backend value instead of form state
                  value: currentValue,
                  onChange: (e) => {
                    controllerField.onChange(e);
                    const values = Array.isArray(e) ? e : [e];

                    field.onChange(
                      values.map((v) =>
                        typeof v === 'string' && !Number.isNaN(Number(v)) ? Number(v) : v,
                      ),
                    );
                  },
                }}
                groupedFields={{ [field.key]: [field as { name?: string }] }}
                index={0}
                error={error}
                groupKey={field.key}
              />
            )}
          />
        </Box>
      );
    }
    case 'CHECKBOX_GROUP': {
      return (
        <Box sx={itemContainerSx}>
          <CheckboxGroup
            key={field.key}
            options={formattedOptions}
            selectedValues={optimisticCheckboxValues}
            setSelectedValues={(values: string[]) => {
              setOptimisticCheckboxValues(values);
              field.onChange(values.map(Number));
            }}
            sxOverrides={cbGroupControlOverrides}
            disabled={determineIfInputDisabled(field, userProfile, profileValueOptions)}
          />
        </Box>
      );
    }

    case 'TEXT': {
      const defaultValue = Array.isArray(field.defaultValue)
        ? field.defaultValue[0] || ''
        : field.defaultValue || '';

      const isAcademicMetric = [
        'gpa',
        'major_gpa',
        'sat_score',
        'act_score',
        'psat_score',
        'high_school_gpa',
        'post_secondary_gpa',
      ].includes(field.key);
      const isUrlField = ['linkedInData', 'calendlyData'].includes(field.key);
      const { multiline, rows } = field.type;

      return (
        <Box key={field.key}>
          <Controller
            name={field.key}
            control={control}
            defaultValue={defaultValue}
            render={({ field: controllerField, fieldState: { error } }) => (
              <ProfileFormTextField
                disabled={determineIfInputDisabled(field, userProfile, profileValueOptions)}
                multiline={multiline}
                rows={rows}
                field={{
                  name: field.key,
                  placeholder: field.placeholder ?? '',
                  customStartAdornment: field.textLeadingAdornment?.name ? (
                    <Box sx={startAdornmentContainerSx}>
                      <Typography
                        variant="text-md-regular"
                        color={(theme) => theme.backrs.colors['Gray/600']}
                        sx={textLeadingSx}
                      >
                        {field.textLeadingAdornment?.name}
                      </Typography>
                    </Box>
                  ) : null,
                  sx: { width: field.width === 'half' ? 'calc(50% - 8px)' : '100%' },
                }}
                groupedFields={{ [field.key]: [field as { name?: string }] }}
                index={0}
                controllerField={{
                  ...controllerField,
                  onChange: (e) => {
                    controllerField.onChange(e);
                    const value = e.target.value;

                    if (validateValue(value, field.validation)) {
                      if (isAcademicMetric) {
                        // For academic metrics, we need to create an object with the specific metric
                        const metricValue = value ? Number(value) : null;
                        const metricObject: AcademicMetricValue = { [field.key]: metricValue };

                        field.onChange?.([metricObject]);
                      } else if (isUrlField) {
                        field.onChange?.(value ? [formatUrl(value)] : ['']);
                      } else {
                        field.onChange?.(value ? [value] : [null]);
                      }
                    }
                  },
                }}
                error={error?.message}
                key={field.key}
                validation={field?.validation}
              />
            )}
          />
        </Box>
      );
    }
    case 'PROFILE_IMAGE': {
      return (
        <Box>
          <ProfileImageField field={field} />
        </Box>
      );
    }

    default:
      return null;
  }
};

const itemContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
};

const dynamicInputsContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: 2,
  width: '100%',
};

const startAdornmentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  paddingRight: backrs.spacing['spacing-md'],
  maxWidth: '175px',
  width: '175px',
});

const textLeadingSx: SxProps<Theme> = {
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  width: '100%',
};

const cbGroupControlOverrides: SxProps<Theme> = ({ backrs, typography }) => ({
  outline: 'none',
  padding: backrs.spacing['spacing-xxs'],
  '&.Mui-disabled .MuiFormControlLabel-label': {
    color: backrs.colors['Colors/Base/Black'],
  },
  '& .MuiFormControlLabel-label': {
    ...typography['text-md-regular'],
    marginTop: backrs.spacing['spacing-none'],
    paddingTop: backrs.spacing['spacing-none'],
  },
  '& .MuiFormControlLabel-root': {
    display: 'flex',
    marginTop: backrs.spacing['spacing-none'],
    paddingTop: backrs.spacing['spacing-none'],
  },
  '& .MuiCheckbox-root': {
    alignSelf: 'flex-start',
  },
  gap: backrs.spacing['spacing-md'],
});
